import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Textarea,
  Switch,
  Text,
  Divider,
  Checkbox,
  SimpleGrid,
} from '@chakra-ui/react';
import { getUser, sendWhatsappTest, updateUser } from '../api/user';
import { getRiskSettings, updateRiskSettings } from '../api/portfolio';

const BLOCKABLE_STRATEGIES = [
  ['rsi2', 'RSI(2) Connors'],
  ['confluence', 'Kairi + Confluência'],
  ['reversal', 'Reversão na Tendência'],
  ['setorial', 'Análise Setorial'],
  ['breakout55', 'Breakout 55/20'],
];
const DEFAULT_RISK_SETTINGS = {
  capital_operacional: '',
  risco_por_trade_pct: 0.75,
  heat_maximo_pct: 4.5,
  heat_por_setor_pct: 1.5,
  heat_por_estrategia_pct: 2.5,
};

const Profile = ({ onClose, userId }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingWhatsappTest, setIsSendingWhatsappTest] = useState(false);
  const [riskSettings, setRiskSettings] = useState(DEFAULT_RISK_SETTINGS);
  const [riskUnavailable, setRiskUnavailable] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await getUser(userId);
        setUser({
          ...userData,
          blockedStrategyAlertKeys: Array.isArray(userData.blockedStrategyAlertKeys)
            ? userData.blockedStrategyAlertKeys
            : [],
        });
      } catch (error) {
        toast({
          title: 'Não foi possível carregar suas configurações',
          description: error.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, [toast, userId]);

  useEffect(() => {
    const fetchRiskSettings = async () => {
      try {
        const data = await getRiskSettings();
        setRiskSettings({ ...DEFAULT_RISK_SETTINGS, ...data });
      } catch (_) {
        setRiskUnavailable(true);
      }
    };
    fetchRiskSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setUser({ ...user, [name]: newValue });
  };

  const handleAlertTypeChange = (type, checked) => {
    const current = Array.isArray(user.realtimeAlertTypes) ? user.realtimeAlertTypes : [];
    setUser({
      ...user,
      realtimeAlertTypes: checked
        ? [...new Set([...current, type])]
        : current.filter(item => item !== type),
    });
  };

  const handleBlockedStrategyChange = (key, checked) => {
    const current = Array.isArray(user.blockedStrategyAlertKeys) ? user.blockedStrategyAlertKeys : [];
    setUser({
      ...user,
      blockedStrategyAlertKeys: checked
        ? [...new Set([...current, key])]
        : current.filter(item => item !== key),
    });
  };

  const handleRiskChange = (event) => {
    const { name, value } = event.target;
    setRiskSettings(current => ({ ...current, [name]: value }));
  };

  const validateRiskSettings = () => {
    const capital = Number(riskSettings.capital_operacional);
    const risk = Number(riskSettings.risco_por_trade_pct);
    const heat = Number(riskSettings.heat_maximo_pct);
    const sector = Number(riskSettings.heat_por_setor_pct);
    const strategy = Number(riskSettings.heat_por_estrategia_pct);
    if (!Number.isFinite(capital) || capital <= 0 || ![risk, heat, sector, strategy].every(Number.isFinite)) {
      return 'Informe valores numéricos válidos para a gestão de risco.';
    }
    if (risk < 0 || risk > 2 || [heat, sector, strategy].some(value => value < 0 || value > 8)) {
      return 'Risco por trade deve ser de até 2% e os limites de heat de até 8%.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (user.whatsappAlertsEnabled && (!user.whatsapp || !user.whatsappConsent)) {
      toast({
        title: 'Complete a autorização do WhatsApp',
        description: 'Para ativar o WhatsApp, informe o número e autorize o recebimento de alertas.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!riskUnavailable) {
      const riskError = validateRiskSettings();
      if (riskError) {
        toast({ title: 'Revise os limites de risco', description: riskError, status: 'warning', duration: 4000, isClosable: true });
        return;
      }
    }
    try {
      await updateUser(user.id, user);
      if (!riskUnavailable) {
        const savedRiskSettings = await updateRiskSettings({
          capital_operacional: Number(riskSettings.capital_operacional),
          risco_por_trade_pct: Number(riskSettings.risco_por_trade_pct),
          heat_maximo_pct: Number(riskSettings.heat_maximo_pct),
          heat_por_setor_pct: Number(riskSettings.heat_por_setor_pct),
          heat_por_estrategia_pct: Number(riskSettings.heat_por_estrategia_pct),
        });
        setRiskSettings({ ...DEFAULT_RISK_SETTINGS, ...savedRiskSettings });
      }
      toast({
        title: 'Preferências atualizadas',
        description: 'Suas configurações de alerta foram salvas.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Não foi possível salvar',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleWhatsappTest = async () => {
    if (!user.whatsappAlertsEnabled || !user.whatsapp || !user.whatsappConsent) {
      toast({
        title: 'Complete a configuração do WhatsApp',
        description: 'Ative os alertas, informe seu celular e autorize o recebimento antes de testar.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setIsSendingWhatsappTest(true);
    try {
      const savedUser = await updateUser(user.id, user);
      setUser(savedUser);
      const result = await sendWhatsappTest(user.id);
      toast({
        title: 'Teste enviado',
        description: result.message,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Não foi possível enviar o teste',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSendingWhatsappTest(false);
    }
  };

  if (isLoading) {
    return <Box>Carregando configurações...</Box>;
  }

  if (!user) {
    return <Box>Não foi possível carregar suas configurações. Feche e abra este painel novamente.</Box>;
  }

  return (
    <Box maxW="md" mx="auto" py={4}>
      <form onSubmit={handleSubmit}>
        <VStack spacing={5} align="stretch">
          <FormControl id="username" isRequired>
            <FormLabel>Nome de usuário</FormLabel>
            <Input
              name="username"
              value={user.username}
              onChange={handleInputChange}
            />
          </FormControl>
          <FormControl id="email">
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              name="email"
              value={user.email}
              disabled={true}
            />
          </FormControl>

          <FormControl id="description">
            <FormLabel>Sobre você</FormLabel>
            <Textarea
              name="description"
              value={user.description || ''}
              onChange={handleInputChange}
              maxLength={4000}
            />
          </FormControl>

          <Divider />
          <Box>
            <Text fontWeight="700" mb={1}>Risco</Text>
            <Text fontSize="sm" color="gray.500" mb={3}>
              Defina o capital operacional e os limites usados pelo Agente de Risco.
            </Text>
            {riskUnavailable ? (
              <Text fontSize="sm" color="orange.500">
                Configurações de risco indisponíveis no momento. Suas preferências de alerta continuam disponíveis.
              </Text>
            ) : (
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                <FormControl isRequired>
                  <FormLabel>Capital operacional (R$)</FormLabel>
                  <Input data-testid="input-risk-operational-capital" type="number" min="0.01" step="0.01" name="capital_operacional" value={riskSettings.capital_operacional} onChange={handleRiskChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Risco por trade (%)</FormLabel>
                  <Input data-testid="input-risk-per-trade" type="number" min="0" max="2" step="0.01" name="risco_por_trade_pct" value={riskSettings.risco_por_trade_pct} onChange={handleRiskChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Heat máximo (%)</FormLabel>
                  <Input data-testid="input-risk-max-heat" type="number" min="0" max="8" step="0.1" name="heat_maximo_pct" value={riskSettings.heat_maximo_pct} onChange={handleRiskChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Heat por setor (%)</FormLabel>
                  <Input data-testid="input-risk-sector-heat" type="number" min="0" max="8" step="0.1" name="heat_por_setor_pct" value={riskSettings.heat_por_setor_pct} onChange={handleRiskChange} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Heat por estratégia (%)</FormLabel>
                  <Input data-testid="input-risk-strategy-heat" type="number" min="0" max="8" step="0.1" name="heat_por_estrategia_pct" value={riskSettings.heat_por_estrategia_pct} onChange={handleRiskChange} />
                </FormControl>
              </SimpleGrid>
            )}
          </Box>

          <Divider />
          <Box>
            <Text fontWeight="700" mb={1}>Alertas de oportunidades</Text>
            <Text fontSize="sm" color="gray.500">
              Receba um aviso apenas quando os cinco critérios da Confluência confirmarem uma entrada.
            </Text>
          </Box>

          <FormControl id="autoMonitoringEnabled">
            <FormLabel mb={1}>Monitorar operações automaticamente</FormLabel>
            <Switch
              name="autoMonitoringEnabled"
              isChecked={!!user.autoMonitoringEnabled}
              onChange={handleInputChange}
            />
            <Text fontSize="sm" color="gray.500" mt={2}>
              Abre e encerra posições na carteira teórica pelo servidor, inclusive com a aba fechada.
              Registra entrada, saída positiva ou stop e envia WhatsApp quando os alertas correspondentes estiverem habilitados.
              Não envia ordens para corretora nem altera posições manuais.
            </Text>
          </FormControl>

          <FormControl id="emailAlertsEnabled">
            <FormLabel mb={1}>Alertas por e-mail</FormLabel>
            <Switch
              name="emailAlertsEnabled"
              isChecked={!!user.emailAlertsEnabled}
              onChange={handleInputChange}
            />
          </FormControl>

          <Box
            border="1px solid"
            borderColor="blackAlpha.200"
            borderRadius="md"
            p={4}
            bg="blackAlpha.50"
          >
            <Text fontWeight="700" mb={1}>Disciplina por estratégia</Text>
            <Text fontSize="sm" color="gray.500" mb={3}>
              Estratégias bloqueadas deixam de abrir alertas automaticamente, mas você pode manter os sinais visíveis para acompanhar o mercado.
            </Text>
            <VStack align="stretch" spacing={2}>
              {BLOCKABLE_STRATEGIES.map(([key, label]) => (
                <Checkbox
                  key={key}
                  isChecked={(user.blockedStrategyAlertKeys || []).includes(key)}
                  onChange={event => handleBlockedStrategyChange(key, event.target.checked)}
                  colorScheme="teal"
                >
                  <Text as="span" fontSize="sm">{label} — Alertar sinais mesmo bloqueada</Text>
                </Checkbox>
              ))}
            </VStack>
          </Box>

          <FormControl id="whatsappAlertsEnabled">
            <FormLabel mb={1}>Alertas por WhatsApp</FormLabel>
            <Switch
              name="whatsappAlertsEnabled"
              isChecked={!!user.whatsappAlertsEnabled}
              onChange={handleInputChange}
            />
          </FormControl>

          <FormControl id="whatsapp" isRequired={!!user.whatsappAlertsEnabled}>
            <FormLabel>Celular que receberá as notificações</FormLabel>
            <Input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              name="whatsapp"
              value={user.whatsapp || ''}
              onChange={handleInputChange}
              placeholder="+55 (11) 99999-9999"
            />
            <Text fontSize="sm" color="gray.500" mt={2}>
              Informe o número com DDI e DDD. Ele só será usado se os alertas por WhatsApp estiverem ativados.
            </Text>
          </FormControl>

          {user.whatsappAlertsEnabled && (
            <>
              <FormControl id="whatsappConsent">
                <FormLabel mb={1}>Autorização de envio</FormLabel>
                <Switch
                  name="whatsappConsent"
                  isChecked={!!user.whatsappConsent}
                  onChange={handleInputChange}
                />
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Autorizo receber no número informado alertas transacionais de oportunidades confirmadas.
                </Text>
              </FormControl>

              <Box>
                <Button
                  type="button"
                  colorScheme="teal"
                  variant="outline"
                  width="full"
                  onClick={handleWhatsappTest}
                  isLoading={isSendingWhatsappTest}
                  loadingText="Enviando teste"
                  isDisabled={!user.whatsapp || !user.whatsappConsent}
                >
                  Enviar mensagem de teste
                </Button>
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Envia o modelo novosinal com dados fictícios para o seu número salvo. Disponível uma vez a cada 5 minutos.
                </Text>
              </Box>

              <FormControl>
                <FormLabel>Tipos enviados por WhatsApp</FormLabel>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                  {[
                    ['breakout', 'Entradas, rompimentos e ordens'],
                    ['stop', 'Stops'],
                    ['target', 'Alvos e saídas positivas'],
                    ['ex_date', 'Datas ex'],
                  ].map(([value, label]) => (
                    <Checkbox
                      key={value}
                      isChecked={(user.realtimeAlertTypes || []).includes(value)}
                      onChange={event => handleAlertTypeChange(value, event.target.checked)}
                    >
                      {label}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </FormControl>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel>Silêncio a partir de</FormLabel>
                  <Input
                    type="time"
                    name="quietHoursStart"
                    value={user.quietHoursStart || ''}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Silêncio até</FormLabel>
                  <Input
                    type="time"
                    name="quietHoursEnd"
                    value={user.quietHoursEnd || ''}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </SimpleGrid>
              <Text fontSize="xs" color="gray.500">
                Horário de Brasília. Alertas ocorridos nesse intervalo continuam disponíveis no sino, mas não são enviados pelo WhatsApp.
              </Text>
            </>
          )}

          <Text fontSize="xs" color="gray.500">
            Os alertas são informativos e não constituem recomendação de investimento. Você pode desativá-los a qualquer momento.
          </Text>

          <Button type="submit" colorScheme="teal" width="full">
            Salvar preferências
          </Button>
        </VStack>
      </form>
    </Box>
  );
};

export default Profile;
